import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import RestHttpClient from '../../../../../common/RestHttpClient';
import { useIdentityPolling } from '../../../../customers/identity/useIdentityPolling';
import { pushNotificationHandler } from '../../../../../common/PushNotification';

interface Props {
  customerId: number;
  dealId: number;
  onCompleted: () => void;
  onCanceled: () => void;
  onReset: () => void;
}

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '-';
  if (value instanceof Date) return value.toLocaleDateString('de-DE');
  return String(value);
};

function ResultField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function ResultGrid({
  checkId,
  checkDate,
  expirationDate,
  bankAccount,
}: {
  checkId: string;
  checkDate: Date | undefined;
  expirationDate: Date | undefined;
  bankAccount: {
    accountHolder?: string;
    iban?: string;
    bic?: string;
    bankName?: string;
    bankCountry?: string;
  } | undefined;
}) {
  const { t } = useTranslation();
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          {t('customers.identityCheck.sections.kontoCheck')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ResultField label={t('customers.identityCheck.labels.orderNumber')} value={formatValue(checkId)} />
          </Grid>
          <Grid item xs={6}>
            <ResultField label={t('customers.identityCheck.labels.orderDate')} value={formatValue(checkDate)} />
          </Grid>
          <Grid item xs={6}>
            <ResultField label={t('customers.identityCheck.labels.validUntil')} value={formatValue(expirationDate)} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          {t('customers.identityCheck.sections.bankAccount')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ResultField label={t('customers.identityCheck.labels.accountHolder')} value={formatValue(bankAccount?.accountHolder)} />
          </Grid>
          <Grid item xs={6}>
            <ResultField label={t('customers.identityCheck.labels.iban')} value={formatValue(bankAccount?.iban)} />
          </Grid>
          <Grid item xs={4}>
            <ResultField label={t('customers.identityCheck.labels.bic')} value={formatValue(bankAccount?.bic)} />
          </Grid>
          <Grid item xs={5}>
            <ResultField label={t('customers.identityCheck.labels.bankName')} value={formatValue(bankAccount?.bankName)} />
          </Grid>
          <Grid item xs={3}>
            <ResultField label={t('customers.identityCheck.labels.bankCountry')} value={formatValue(bankAccount?.bankCountry)} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export function FincredibleSection({
  customerId,
  dealId,
  onCompleted,
  onCanceled,
  onReset,
}: Props) {
  const { t } = useTranslation();
  const [started, setStarted] = useState(false);
  const [initialChecking, setInitialChecking] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // On mount: silently check for an existing check. If one exists (any non-terminal
  // state), auto-resume it so the user never needs to press Start again for an
  // in-progress or already-completed check. No check is created here.
  useEffect(() => {
    let cancelled = false;
    const INVALID = ['expired', 'cancelled', 'discarded'];
    (async () => {
      try {
        const result = await RestHttpClient.getIdentityCheckByCustomerId(customerId);
        if (cancelled) return;
        const status = result.data?.status?.toLowerCase() ?? '';
        if (result.data && !INVALID.includes(status)) {
          setStarted(true);
        }
      } catch {
        // no existing check — stay idle, show Start button
      } finally {
        if (!cancelled) setInitialChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const { data, loading } = useIdentityPolling(customerId, started, dealId);

  const status = data?.status?.toLowerCase();
  const isAccepted = status === 'accepted' || status === 'completed';
  const nameCheckVerified = data?.details?.nameCheckVerified;

  const handleConfirm = async () => {
    if (!data?.checkId) return;
    setIsProcessing(true);
    try {
      await RestHttpClient.completeIdentityCheck(data.checkId, customerId, dealId);
      onCompleted();
      pushNotificationHandler.publish(t('customers.identityCheck.confirmSuccess'), 'success');
    } catch (error) {
      console.error('Failed to confirm identity check:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!data?.checkId) return;
    setShowCancelConfirm(false);
    setIsProcessing(true);
    try {
      await RestHttpClient.cancelIdentityCheck(data.checkId, customerId);
      setStarted(false);
      onCanceled();
    } catch (error) {
      console.error('Failed to cancel identity check:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setStarted(false);
    onReset();
  };

  // Still checking for an existing check on mount
  if (initialChecking) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Idle — no existing check found, user must start explicitly
  if (!started) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('identityCheck.fincredible.description')}
        </Typography>
        <Button variant="contained" onClick={() => setStarted(true)}>
          {t('identityCheck.fincredible.start')}
        </Button>
      </Box>
    );
  }

  // Loading initial check
  if (loading && !data) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('customers.identityCheck.loading')}
        </Typography>
      </Box>
    );
  }

  // Polling — waiting for user in Fincredible app
  if (data && !isAccepted) {
    return (
      <Box>
        <Box display="flex" flexDirection="column" alignItems="center" py={3}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('customers.identityCheck.waitingForAcceptance')}
          </Typography>
          {data.checkId && (
            <Typography variant="caption" sx={{ mt: 1 }} color="text.secondary">
              {t('customers.identityCheck.checkId')}: {data.checkId}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" color="secondary" disabled={isProcessing} onClick={() => setShowCancelConfirm(true)}>
            {t('customers.identityCheck.cancel')}
          </Button>
        </Box>
        <CancelConfirmDialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} onConfirm={handleConfirmCancel} />
      </Box>
    );
  }

  // Completed — show results
  if (data && isAccepted) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {nameCheckVerified === false ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle1" color="warning.main">
                {t('customers.identityCheck.nameCheckFailed')}
              </Typography>
            </Box>
          ) : status === 'completed' ? (
            <Typography variant="subtitle1" color="success.main">
              {t('customers.identityCheck.completed')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle1" color="warning.main">
                {t('customers.identityCheck.accepted')}
              </Typography>
            </Box>
          )}
        </Box>
        <ResultGrid
          checkId={data.checkId}
          checkDate={data.checkDate}
          expirationDate={data.expirationDate}
          bankAccount={data.details?.bankAccount}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="secondary" disabled={isProcessing} onClick={() => setShowCancelConfirm(true)}>
            {t('customers.identityCheck.cancel')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={isProcessing}
            onClick={handleReset}
          >
            {t('identityCheck.reset')}
          </Button>
          <Button
            variant="contained"
            disabled={isProcessing || status === 'completed' || nameCheckVerified === false}
            onClick={handleConfirm}
          >
            {t('customers.identityCheck.confirm')}
          </Button>
        </Box>
        <CancelConfirmDialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} onConfirm={handleConfirmCancel} />
      </Box>
    );
  }

  return null;
}

function CancelConfirmDialog({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('customers.identityCheck.cancelConfirmTitle')}</DialogTitle>
      <DialogContent>
        <Typography>{t('customers.identityCheck.cancelConfirmMessage')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">
          {t('customers.identityCheck.cancelConfirmNo')}
        </Button>
        <Button onClick={onConfirm} variant="contained">
          {t('customers.identityCheck.cancelConfirmYes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
